import '../../tooltip/index.js';
import {html} from 'lit';
import {repeat} from 'lit/directives/repeat.js';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '#lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '#lit/components/mixins/immutable_component/index.js';
import type {VariantsFor} from '#lit/types/architecture.js';
import {
  AudienceFrequencyEnum,
  audienceSyncFrequencyEnumLabels,
} from '#lit/types/journey/audience_frequency.js';
import type {CustomSelectEvent} from '#lit/components/base/select/index.js';
import {SelectVariant} from '#lit/components/base/select/index.js';
import {
  dispatchChangeEvent,
  getDefaultFrequencyOptions,
  normalizeValue,
} from './utils.js';

// -- Component dependencies
import '#lit/components/base/list_item/index.js';
import type {AudienceDatasetIds} from '#lit/contexts/audience.js';
import type {Nullable} from '#types';

export type Model = {
  form: string;
  name: string;
  value: AudienceFrequencyEnum | '';
  defaultValue: AudienceFrequencyEnum | '';
  hidden: boolean;
  disabled: boolean;
  required: boolean;
  options: AudienceFrequencyEnum[];
  audience: Nullable<AudienceDatasetIds>;
};

// export type CModel = ComputedModel<Model>;

export const Msg = makeVariants({
  setValue: Variant<AudienceFrequencyEnum | ''>()('set-value'),
  setHidden: Variant<boolean>()('set-hidden'),
  setDisabled: Variant<boolean>()('set-disabled'),
  setRequired: Variant<boolean>()('set-required'),
  setOptions: Variant<AudienceFrequencyEnum[]>()('set-options'),
  setAudience: Variant<Nullable<AudienceDatasetIds>>()('set-audience'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(props: InitialProperties, _cmd: Cmd, model?: Model) {
  const audience = <AudienceDatasetIds>props.get('audience') ?? null;
  const form = <string | undefined>props.get('form') ?? '';
  const name = <string | undefined>props.get('name') ?? '';
  const value =
    <AudienceFrequencyEnum | undefined>props.get('value') ??
    model?.value ??
    ('' as const);
  const defaultValue =
    <AudienceFrequencyEnum | undefined>props.get('defaultValue') ??
    model?.defaultValue ??
    ('' as const);
  const hidden = <boolean | undefined>props.get('hidden') ?? false;
  const disabled = <boolean | undefined>props.get('disabled') ?? false;
  const required = <boolean | undefined>props.get('required') ?? false;
  const options =
    <AudienceFrequencyEnum[] | undefined>props.get('options') ??
    getDefaultFrequencyOptions();

  const updatedModel = {
    audience,
    form,
    name,
    value: normalizeValue(value, options),
    defaultValue: normalizeValue(defaultValue, options),
    hidden,
    disabled,
    required,
    options,
  };

  return updatedModel;
}

// -- Update

function update(msg: Msg, model: Model, cmd: Cmd): ImmutableResult<Model> {
  switch (msg.tag) {
    case 'set-value': {
      if (model.value === msg.value) return model;
      return [{...model, value: msg.value}, cmd(dispatchChangeEvent)];
    }
    case 'set-audience':
      return {...model, audience: msg.value};
    case 'set-disabled':
      return {...model, disabled: msg.value};

    case 'set-hidden':
      return {...model, hidden: msg.value};

    case 'set-required': {
      return {...model, required: msg.value};
    }

    case 'set-options':
      return {...model, options: msg.value};
  }
}

// -- View

function view(model: Model, handleUpdate: UpdateHandler<Model, Msg>) {
  return html`
    <label> Frequency </label>
    <fly-select
      selectionRequired
      variant=${SelectVariant.CONDENSED_ROUNDED_SQUARE}
      initial=${model.value !== '' ? model.value : model.defaultValue}
      ?disabled=${model.disabled}
      ?hidden=${model.hidden}
      @selected=${(
        event: CustomEvent<CustomSelectEvent<AudienceFrequencyEnum>>
      ) => {
        event.stopPropagation();
        handleUpdate(Msg.setValue(event.detail.value));
      }}
    >
      ${repeat(
        model.options,
        frequency => frequency,
        frequency => renderFrequencyOption(frequency, model)
      )}
    </fly-select>
  `;
}

function renderFrequencyOption(frequency: AudienceFrequencyEnum, model: Model) {
  const realtime = model.audience?.realtime;
  if (frequency === AudienceFrequencyEnum.REALTIME) {
    if (!realtime?.available && realtime?.reason) {
      return html`
        <fly-tooltip content=${realtime.reason}>
          <fly-list-item
            ?disabled=${true}
            value=${frequency}
            ?inert=${model.value === frequency}
          >
            ${audienceSyncFrequencyEnumLabels[frequency]}
          </fly-list-item>
        </fly-tooltip>
      `;
    }
    if (!realtime?.available) {
      return;
    }
  }
  return html`
    <fly-list-item value=${frequency} ?inert=${model.value === frequency}>
      ${audienceSyncFrequencyEnumLabels[frequency]}
    </fly-list-item>
  `;
}

// -- Program

export const main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
